import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate, useSearchParams } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { authAPI } from "../../services/api";
import CustomButton from "../ui/CustomButton";

const BackgroundBox = styled(Box)(({ theme }) => ({
  minHeight: "90vh",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: theme.spacing(2),
  paddingTop: "8vh",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1.5),
    backgroundColor: "transparent",
    "& .MuiOutlinedInput-input": {
      backgroundColor: "transparent",
    },
  },
  "& .MuiInputLabel-root": {
    backgroundColor: "transparent",
  },
}));

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    const errors = {};

    if (!password) {
      errors.password = "Password is required";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters long";
    }

    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (!token) {
      setError("Invalid or missing reset token. Please request a new password reset.");
      return;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.resetPassword(token, password);
      if (response.data) {
        setSuccess(true);
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to reset password. The link may have expired. Please request a new password reset.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <BackgroundBox>
        <Box
          sx={{
            padding: 4,
            width: "100%",
            maxWidth: 400,
            borderRadius: 4,
            textAlign: "center",
            backgroundColor: "transparent",
          }}
        >
          <Typography variant="h5" fontWeight="500" sx={{ mb: "2rem" }}>
            Invalid Reset Link
          </Typography>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            This password reset link is invalid or has expired. Please request a
            new password reset.
          </Alert>
          <CustomButton
            fullWidth
            variant="contained"
            onClick={() => navigate("/forgot-password")}
            sx={{
              height: "50px",
              borderRadius: "25px",
              textTransform: "none",
              mt: 2,
            }}
          >
            Request New Reset Link
          </CustomButton>
          <Box
            onClick={() => navigate("/login")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              mt: 3,
              color: "text.secondary",
              "&:hover": {
                color: "black",
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16, mr: 1 }} />
            <Typography variant="body2">Back to Login</Typography>
          </Box>
        </Box>
      </BackgroundBox>
    );
  }

  if (success) {
    return (
      <BackgroundBox>
        <Box
          sx={{
            padding: 4,
            width: "100%",
            maxWidth: 400,
            borderRadius: 4,
            textAlign: "center",
            backgroundColor: "transparent",
          }}
        >
          <Typography variant="h5" fontWeight="500" sx={{ mb: "2rem" }}>
            Password Reset Successful
          </Typography>
          <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
            Your password has been reset successfully. Redirecting to login...
          </Alert>
        </Box>
      </BackgroundBox>
    );
  }

  return (
    <BackgroundBox>
      <Box
        sx={{
          padding: 4,
          width: "100%",
          maxWidth: 400,
          borderRadius: 4,
          textAlign: "center",
          backgroundColor: "transparent",
        }}
      >
        <Typography variant="h5" fontWeight="500" sx={{ mb: "2rem" }}>
          Reset Password
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: "left" }}
        >
          Please enter your new password below.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <StyledTextField
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (fieldErrors.password) {
                setFieldErrors((prev) => ({ ...prev, password: "" }));
              }
            }}
            fullWidth
            autoFocus
            placeholder="New Password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={!!fieldErrors.password}
            helperText={
              fieldErrors.password || "Password must be at least 6 characters"
            }
          />

          <StyledTextField
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (fieldErrors.confirmPassword) {
                setFieldErrors((prev) => ({ ...prev, confirmPassword: "" }));
              }
            }}
            fullWidth
            placeholder="Confirm New Password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleConfirmPasswordVisibility}
                    edge="end"
                    aria-label="toggle confirm password visibility"
                  >
                    {showConfirmPassword ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={!!fieldErrors.confirmPassword}
            helperText={fieldErrors.confirmPassword}
          />

          <CustomButton
            fullWidth
            variant="contained"
            type="submit"
            size="large"
            disabled={loading}
            preventSpam={true}
            spamDelay={2000}
            sx={{
              height: "50px",
              borderRadius: "25px",
              textTransform: "none",
              mt: 2,
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Reset Password"
            )}
          </CustomButton>

          <Box
            onClick={() => navigate("/login")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              mt: 3,
              color: "text.secondary",
              "&:hover": {
                color: "black",
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16, mr: 1 }} />
            <Typography variant="body2">Back to Login</Typography>
          </Box>
        </form>
      </Box>
    </BackgroundBox>
  );
};

export default ResetPassword;


